import { Stack, TextField, Autocomplete } from '@mui/material';
import _ from 'lodash';
import { type FC, useMemo } from 'react';
import { Controller } from 'react-hook-form';

import { usePatients } from '../../api/index.js';
import { useCreateNvr, useOneNvr, useUpdateNvr } from '../../api/nvrs.api.js';
import { type Patient } from '../../models/index.js';
import AutocompleteOptions from '../AutocompleteOptions.js';
import FormDialog from '../Forms/FormDialog.js';
import { nvrFormSchema } from '../Forms/validations.js';

interface NvrFormValues {
  name: string;
  ipAddress: string;
  onvifPort: string;
  rtspPort: string;
  httpPort?: string;
  username: string;
  password: string;
  patientId?: string;
}

const defaultValues: NvrFormValues = {
  name: '',
  ipAddress: '',
  onvifPort: '',
  rtspPort: '',
  httpPort: '',
  username: '',
  password: '',
  patientId: '',
};

interface NvrFormProps {
  open: boolean;
  onClose: () => void;
  nvrId?: string;
  refetch: () => void;
  patientId?: string;
}

const NvrForm: FC<NvrFormProps> = ({open, onClose, nvrId, refetch, patientId}) => {
  const {data} = useOneNvr(nvrId, {enabled: !!nvrId});
  const {data: patientsData = []} = usePatients();
  console.log(patientId);
  const createMutation = useCreateNvr();
  const updateMutation = useUpdateNvr();

  const formattedData: NvrFormValues = useMemo(() => {
    if (data) {
      return {
        name: data.name,
        ipAddress: data.ipAddress,
        onvifPort: data.onvifPort,
        rtspPort: data.rtspPort,
        httpPort: data.httpPort,
        username: data.username,
        password: data.password,
        patientId: patientId,
      };
    }
    return {
      ...defaultValues,
      patientId: patientId,
    };
  }, [data, patientId]);

  return (
    <FormDialog
      open={open}
      name={'NVR'}
      onClose={onClose}
      data={formattedData}
      schema={nvrFormSchema}
      isEditing={!!data}
      onSubmit={async ({...payload}, isEditing) => {
        if (isEditing) {
          await updateMutation.mutateAsync({id: nvrId!, ...payload});
        } else {
          await createMutation.mutateAsync({...payload});
        }
      }}
      refetch={refetch}
    >
      {(control, errors) => (
        <Stack spacing={2} sx={{mt: 2}}>
          <Controller
            name={'name'}
            control={control}
            render={({field}) => (
              <TextField {...field} InputLabelProps={{required: true}} error={!!errors?.name}
                         helperText={errors?.name?.message || ''} label={'Name'}/>
            )}
          />
          <Controller
            name={'ipAddress'}
            control={control}
            render={({field}) => (
              <TextField {...field} InputLabelProps={{required: true}} error={!!errors?.ipAddress}
                         helperText={errors?.ipAddress?.message || ''} label={'IP Address'}/>
            )}
          />
          <Controller
            name={'onvifPort'}
            control={control}
            render={({field}) => (
              <TextField {...field} InputLabelProps={{required: true}} error={!!errors?.port}
                         helperText={errors?.port?.message || ''} label={'Onvif Port'}/>
            )}
          />
          <Controller
            name={'rtspPort'}
            control={control}
            render={({field}) => (
              <TextField {...field} InputLabelProps={{required: true}} error={!!errors?.port}
                         helperText={errors?.port?.message || ''} label={'RTSP Port'}/>
            )}
          />

          <Controller
            name={'httpPort'}
            control={control}
            render={({field}) => (
              <TextField {...field} InputLabelProps={{required: false}} error={!!errors?.port}
                         helperText={errors?.port?.message || ''} label={'HTTP Port'}/>
            )}
          />
          <Controller
            name={'username'}
            control={control}
            render={({field}) => (
              <TextField {...field} InputLabelProps={{required: true}} error={!!errors?.username}
                         helperText={errors?.username?.message || ''} label={'Username'}/>
            )}
          />
          <Controller
            name={'password'}
            control={control}
            render={({field}) => (
              <TextField {...field} InputLabelProps={{required: true}} type={'password'} error={!!errors?.password}
                         helperText={errors?.password?.message || ''} label={'Password'}/>
            )}
          />
          <Controller
            name={'patientId'}
            control={control}
            render={({field}) => (
              <Autocomplete
                value={patientsData.find((patient) => patient.id === field.value)}
                onChange={(_, value) => field.onChange(value?.id)}
                id={'patient-select'}
                options={patientsData}
                isOptionEqualToValue={(option, value) => option?.id === value.id}
                getOptionLabel={(patient: Patient) => (!_.isEmpty(patient) ? `${patient.firstName} ${patient.lastName}` : '')}
                renderInput={(params) => <TextField {...params} label={'Patient'}/>}
                renderOption={({key, ...props}, option, {inputValue}) => (
                  <AutocompleteOptions option={`${option.firstName} ${option.lastName}`} value={inputValue}
                                       key={key} {...props} />
                )}
              />
            )}
          />
        </Stack>
      )}
    </FormDialog>
  );
};

export default NvrForm;
